import Typography from '~/shared/components/Typography/Typography';
import styles from './Card.module.scss';
import { Button, Link } from '~/shared/components';
import clsx from 'clsx';
import { WaterMarkLogo, WaterMarkPercent } from '~/shared/components/WaterMark/WaterMarks';
import { ButtonColorProps, ContentCard } from '~/lib/data-contract/auto-generated/api-model';
import { useMemo } from 'react';
import { DynamicImageLoader } from '~/shared/components/DynamicImageLoader';

type CardProp = ContentCard & {
    className?: string;
    imageRatio?: 'square' | 'rectangle';
    isTextLimit?: boolean;
};

const Card = ({
    className,
    imageRatio,
    imageOnBackground,
    header,
    trumpet,
    paragraph,
    link,
    linkType,
    image,
    imageAltText,
    alias,
    cardTrumpet,
    cardTitle,
    cardText,
    watermark,
    isTextLimit,
}: CardProp) => {
    const variantLink = useMemo(() => {
        let linkVariant: ButtonColorProps = 'new-link';

        if (linkType === 'Secondary') linkVariant = 'new-secondary';
        else if (linkType === 'Primary') linkVariant = 'primary';

        return linkVariant;
    }, [linkType]);

    const isImageOnBackground = imageOnBackground || alias === 'contentCardText';

    const renderWaterMark = () => {
        if (!watermark) return null;

        if (watermark === 'Logo')
            return <WaterMarkLogo className={clsx(styles.waterMark, styles.logo)} />;

        return <WaterMarkPercent className={styles.waterMark} />;
    };

    return (
        <div className={clsx(styles.component, className)}>
            <div
                className={clsx(styles.imageContainer, {
                    [styles.imageOnBackground]: isImageOnBackground,
                    [styles.square]: imageRatio === 'square',
                    [styles.rectangle]: imageRatio === 'rectangle',
                })}
            >
                {isImageOnBackground && renderWaterMark()}

                {image?.src && (
                    <DynamicImageLoader
                        className={styles.image}
                        src={image?.src ?? ''}
                        width={308}
                        height={410}
                        alt={imageAltText ?? ''}
                    />
                )}

                {alias === 'contentCardText' && (
                    <div className={styles.cardText}>
                        {cardTrumpet && (
                            <Typography variant="trumpet-XL" as="p" className={styles.cardTrumpet}>
                                {cardTrumpet}
                            </Typography>
                        )}

                        {cardTitle && (
                            <Typography variant="display-XL" as="p" className={styles.cardTitle}>
                                {cardTitle}
                            </Typography>
                        )}

                        {cardText && (
                            <Typography variant={cardTitle ? 'title-XS' : 'title-M'} as="p">
                                {cardText}
                            </Typography>
                        )}
                    </div>
                )}
            </div>

            <div className={styles.textContainer}>
                {trumpet && (
                    <Typography as="p" variant="paragraph-S" className={styles.trumpet}>
                        {trumpet}
                    </Typography>
                )}

                {header && (
                    <Typography as="h3" variant="title-XS">
                        {header}
                    </Typography>
                )}

                {paragraph && (
                    <Typography
                        as="p"
                        variant="paragraph-M"
                        className={isTextLimit ? styles.paragraph : ''}
                    >
                        {paragraph}
                    </Typography>
                )}

                {link?.url && (
                    <p className={styles.button}>
                        <Button
                            as={Link}
                            aria-label="link"
                            variant={variantLink}
                            href={link.url}
                            target={link.target}
                        >
                            {link.title}
                        </Button>
                    </p>
                )}
            </div>
        </div>
    );
};

export default Card;
